import { useMemo, useState } from 'react';
import { Alert, Box, Button, Divider, Paper, Stack, Text } from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconPaperclip } from '@tabler/icons-react';
import { AgentStreamErrorKind, Artifact } from '~api';
import { getAppEnv } from '~portal/env';
import { artifactDownloadName, latestRevisions } from '~portal/lib';
import {
  ArtifactLineageIndex,
  ClientEventGroup,
  itemFileSize,
  turnArtifactItems,
} from '~portal/stores';
import {
  ArtifactFileRow,
  ArtifactFilesTable,
  ArtifactViewerProvider,
  MdContent,
  NarrationDisplay,
  OperationLine,
  ResponseBlock,
  ThinkingDisplay,
  TurnFilesProvider,
  ViewerImage,
} from './blocks';
import { PromptActions, ResponseActions } from './MessageActions';
import { ResponseActionsWithFeedback } from './ResponseFeedback';
import * as styles from './TurnGroup.css.ts';

/**
 * @desc How each class of turn failure is framed. The backend decides the *kind* and writes the
 * message; the client only decides how loudly to say it.
 *
 * A refused allowance is presented in a neutral colour on purpose — the user did nothing wrong and
 * nothing is broken, so red would misdescribe it as a fault and train people to ignore red.
 */
const ERROR_PRESENTATION: Record<AgentStreamErrorKind, { title: string; colour: string }> = {
  [AgentStreamErrorKind.Quota]: { title: 'Usage limit reached', colour: 'yellow' },
  [AgentStreamErrorKind.Denied]: { title: 'Not permitted', colour: 'orange' },
  [AgentStreamErrorKind.NotFound]: { title: 'Conversation unavailable', colour: 'orange' },
  [AgentStreamErrorKind.Unavailable]: { title: 'The agent is unreachable', colour: 'orange' },
  [AgentStreamErrorKind.Internal]: { title: 'Something went wrong', colour: 'red' },
  // Neutral for the same reason as a refused allowance: the reply simply did not fit, and the
  // remedy is to ask again for something smaller rather than to report a fault.
  [AgentStreamErrorKind.Truncated]: { title: 'Response cut short', colour: 'yellow' },
};

type TurnGroupProps = {
  group: ClientEventGroup;
  isLatest?: boolean;
  isActive?: boolean;
  /**
   * @desc The persisted conversation the turn belongs to, when the viewer may rate its responses
   * (H2-205). Undefined for a read-only viewer or a conversation not yet persisted — the thumbs
   * are then not offered at all.
   */
  feedbackConversationId?: string;
  /**
   * @desc What the rest of the conversation says about this turn's deliverables (ADR-21): the
   * highest revision each lineage later reached, and the names a `derivedFrom` pointer resolves
   * to. Optional because a turn renders correctly without it — it only loses the cross-turn
   * marker, never the artefact it delivered.
   */
  lineage?: ArtifactLineageIndex;
};

export function TurnGroup({
  group,
  isLatest = false,
  feedbackConversationId,
  lineage,
}: TurnGroupProps) {
  /**
   * `VITE_RENDER_TASK_DETAILS=false` withholds the working trace: the tool/skill rows and the
   * model's between-tool narration ("My description string was too long. Retrying with a
   * shorter description."). Both are the turn narrating its own mechanics, so hiding one and
   * keeping the other would leave a running commentary on steps the reader can no longer see.
   * The final answer, the prompt, the files disclosure and the error card are untouched — the
   * flag withholds the trace, not the turn.
   *
   * The gate sits at the call site rather than inside `OperationLine`/`NarrationDisplay` —
   * unlike their sibling `ThinkingDisplay`, which is a whole disclosure and can hide itself —
   * because each one's footprint is its wrapper's padding and margin as much as its content: a
   * self-hiding child would leave an empty padded box per step, i.e. a column of blank gaps
   * where the trace used to be.
   */
  const { taskDetails: showTaskDetails, modelThinking: showThinking } = getAppEnv().render;

  /**
   * Whether this turn's answer is worth composing on arrival.
   *
   * The section reveal exists to stage an answer *landing* in front of someone waiting for it.
   * A conversation opened from history has no arrival to stage — every turn in it is finished —
   * and replaying the stagger there reads as the page assembling itself, several times over.
   *
   * The distinction is made once, at mount, and never revisited: a row that mounted with an
   * answer already in it was rendered from the log, and a row that mounted without one is
   * waiting for its answer to arrive. Lazy state rather than a ref so the decision cannot be
   * accidentally reassigned by a later render.
   */
  const [revealAnswer] = useState(() => !group.response);

  const allItems = turnArtifactItems(group);
  const [filesOpen, setFilesOpen] = useState(false);

  /**
   * What this turn delivered, as delivered.
   *
   * Never a flatten of every item with an artefact behind it: a turn that fixes a chart re-emits
   * every sibling output, so the raw items of one turn legitimately contain two renditions of
   * one deliverable and two references to another. `latestRevisions` is the single fold that
   * knows which of them is current (ADR-21) — the files disclosure, the image carousel and an
   * admin view all reach for the same function, because a second implementation is how they
   * drift.
   *
   * `json` items are excluded: they are the model's own working data, summarised in the
   * operation row, and were never offered as a deliverable.
   */
  const files: ArtifactFileRow[] = useMemo(
    () =>
      latestRevisions(allItems)
        .filter((item) => item.format !== 'json')
        .map((item) => {
          const { artifact } = item;
          const name =
            artifact?.name !== undefined
              ? `${artifact.name}.${item.format}`
              : artifactDownloadName(item.artifactId, item.format);
          const derivedFromLabel = artifact?.derivedFrom
            ? lineage?.labelByArtifactId.get(artifact.derivedFrom)
            : undefined;
          const fileSize = itemFileSize(item.summary);

          return {
            artifactId: item.artifactId,
            name,
            format: item.format,
            revision: artifact?.revision ?? 1,
            isImage: Artifact.isImageFormat(item.format),
            ...(artifact?.description ? { description: artifact.description } : {}),
            ...(derivedFromLabel ? { derivedFromLabel } : {}),
            ...(fileSize !== undefined ? { fileSize } : {}),
          };
        }),
    // `allItems` is re-flattened from `group` on every render, so the fold's inputs are the
    // turn itself and the conversation's lineage.
    [group, lineage],
  );

  /**
   * The carousel's contents: this turn's current images, in event order (ADR-22 decision 7).
   * One viewer per turn, opened at whichever image was clicked — inline embed, chip, card or
   * table row — rather than a separate dead-end modal per surface.
   */
  const viewerImages: ViewerImage[] = useMemo(
    () =>
      files
        .filter((file) => file.isImage)
        .map((file) => ({
          artifactId: file.artifactId,
          downloadName: file.name,
          revision: file.revision,
          ...(file.description ? { description: file.description } : {}),
        })),
    [files],
  );

  // Collect markdown output items for direct display; model-context-only items
  // (summary.display === false, e.g. loaded skills) stay out of the conversation
  const markdownItems = allItems.reduce<string[]>((acc, item) => {
    if (
      item.format === 'markdown' &&
      item.summary?.display !== false &&
      typeof item.data === 'string'
    )
      acc.push(item.data);
    return acc;
  }, []);

  /**
   * The disclosure itself, offered whenever the turn produced files — including when the answer
   * carries a `::files` summary of its own. The two are not duplicates: the summary presents the
   * deliverables as cards inside the answer, and the disclosure is the tabular view of the same
   * files, with the descriptions and sizes side by side, that a reader reaches for to compare
   * them. Hiding it behind the summary took that away from exactly the answers that have most
   * files to compare. It lives *in* the action
   * row so the controls stay the response's last element, and it remembers nothing: the table is
   * a secondary view of files the prose already presented, so it opens closed every time.
   */
  const filesToggle =
    files.length > 0 ? (
      <Button
        variant="subtle"
        color="gray"
        size="compact-xs"
        leftSection={<IconPaperclip size={14} />}
        rightSection={filesOpen ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
        aria-expanded={filesOpen}
        data-dd-action-name="Toggle files list"
        onClick={() => setFilesOpen((open) => !open)}
      >
        {files.length} {files.length === 1 ? 'file' : 'files'}
      </Button>
    ) : null;

  return (
    <ArtifactViewerProvider images={viewerImages}>
      <TurnFilesProvider files={files}>
        <Box className={styles.turnContainer}>
          <Stack gap="sm">
            {/* User prompt */}
            {group.prompt && (
              <Box className={styles.userPrompt}>
                <Paper p="md" radius="lg" className={styles.userBubble}>
                  <Text size="sm">{group.prompt}</Text>
                </Paper>
                <PromptActions timestamp={group.tStart} />
              </Box>
            )}

            {/* Timeline: chronological narrations with their operations */}
            {group.timeline.map((narrationGroup, idx) => {
              // The same three gates the children apply, decided here: ThinkingDisplay returns
              // null under its own flag, and a wrapper around three nulls is still a flex child.
              const hasThinking = showThinking && Boolean(narrationGroup.thinking?.text);
              const showNarration = showTaskDetails && Boolean(narrationGroup.narration.text);
              const showOperations = showTaskDetails && narrationGroup.operations.length > 0;
              // A step with nothing to show renders nothing at all. The Stack pays its gap per
              // child, so a collapsed trace of a dozen empty boxes put a dozen gaps — 150px of
              // nothing — between the prompt and the answer it was hiding the steps of.
              if (!hasThinking && !showNarration && !showOperations) return null;
              return (
                <Box key={idx}>
                  {/* Interleaved reasoning that preceded this step */}
                  {narrationGroup.thinking && (
                    <ThinkingDisplay
                      text={narrationGroup.thinking.text}
                      tElapsed={narrationGroup.thinking.tElapsed}
                    />
                  )}

                  {/* Narration text (if present and not the active latest) */}
                  {showNarration && (
                    <Box className={styles.narrationText} mb="xs">
                      <NarrationDisplay text={narrationGroup.narration.text} isActive={false} />
                    </Box>
                  )}

                  {/* Operations for this narration */}
                  {showOperations && (
                    <Box className={styles.operationsSection}>
                      <Stack gap={4}>
                        {narrationGroup.operations.map((op, opIdx) => (
                          <OperationLine
                            key={op.call.id || opIdx}
                            call={op.call}
                            result={op.result}
                            // The authoring span belongs to the model call, not to any single
                            // tool call it emitted. One model call can emit several, which are
                            // then dispatched together, so charging each row `tFinish -
                            // stepStart` reports the same seconds once per row: four parallel
                            // skill loads all showed the model's ~3.8s, and the rows summed to
                            // far more than the turn took. Only a sole operation can be credited
                            // with the step it was the whole of; the rest report their own
                            // execution time, which the backend measures per invocation.
                            stepStart={
                              narrationGroup.operations.length === 1 ? op.stepStart : undefined
                            }
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>
              );
            })}

            {/* Markdown output (direct display from Python output variable) */}
            {markdownItems.length > 0 && (
              <Box className={`${styles.responseSection} ${styles.markdownOutput}`} py="sm">
                {markdownItems.map((text, idx) => (
                  <MdContent key={idx} text={text} />
                ))}
              </Box>
            )}

            {/* Final response. Its action row is the last thing a response renders (ADR-22): the
            files disclosure expands ABOVE the controls rather than displacing them. */}
            {group.response && (
              <Box className={styles.responseSection}>
                <ResponseBlock text={group.response.text} reveal={revealAnswer} />
                {!group.isThinking && filesOpen && <ArtifactFilesTable rows={files} />}
                {!group.isThinking &&
                  (feedbackConversationId && group.response.messageId ? (
                    <ResponseActionsWithFeedback
                      text={group.response.text}
                      timestamp={group.response.timestamp}
                      conversationId={feedbackConversationId}
                      messageId={group.response.messageId}
                      trailing={filesToggle}
                    />
                  ) : (
                    <ResponseActions
                      text={group.response.text}
                      timestamp={group.response.timestamp}
                      trailing={filesToggle}
                    />
                  ))}
              </Box>
            )}

            {/* Why the turn ended without an answer — a refused allowance, a dropped stream. Last in
            the turn so it reads as the outcome, and rendered even when the timeline is empty, which
            is the case for a turn refused before it started. */}
            {group.error && (
              <Alert
                color={ERROR_PRESENTATION[group.error.kind].colour}
                variant="light"
                radius="md"
                title={ERROR_PRESENTATION[group.error.kind].title}
              >
                <Text size="sm">{group.error.message}</Text>
                {group.error.retryable && (
                  <Text size="xs" mt={4} opacity={0.75}>
                    Send your message again to retry.
                  </Text>
                )}
              </Alert>
            )}
          </Stack>

          {/* Subtle separator between turns */}
          {!isLatest && <Divider mt="md" opacity={0.3} />}
        </Box>
      </TurnFilesProvider>
    </ArtifactViewerProvider>
  );
}
